import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Zap, Eye, RefreshCw } from "lucide-react";
import PullToRefresh from "react-simple-pull-to-refresh";
import { useAuth } from "../../../context/AuthContext";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/games`;
const SPECTATE_API_URL = `${API_BASE_URL}/spectate`;

const renderGameStatus = (status) => {
  const displayStatus = status.toLowerCase();
  switch (status) {
    case "upcoming":
      return (
        <span className="game-status-badge status-upcoming">UPCOMING</span>
      );
    case "READY":
    case "PENDING":
      return <span className="status-warning">{status.toUpperCase()}</span>;
    case "in-progress":
      return <span className="status-live-ongoing">IN-PROGRESS</span>;
    case "paused":
      return <span className="status-paused">PAUSED</span>;
    case "finished":
      return <span className="status-finished">FINISHED</span>;
    default:
      return (
        <span className="game-status-badge">{displayStatus.toUpperCase()}</span>
      );
  }
};

const getSpectateVirtualStatus = (game) => {
  if (game.status === "in-progress" && game.created_at) {
    const startTime = new Date(
      game.created_at.endsWith("Z") ? game.created_at : `${game.created_at}Z`,
    ).getTime();
    if (startTime > Date.now()) {
      return "upcoming";
    }
  }
  return game.status;
};

function SpectateTableContent() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const fetchGames = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(SPECTATE_API_URL);
      setGames(response.data.games || []);
      setError(null);
    } catch (err) {
      setError("Failed to load games. Please check your connection.");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const handleSpectateGame = (clanId, eventId, gameId) => {
    let path = isAuthenticated
      ? `/games/clan/${clanId}/events/${eventId}/game/${gameId}`
      : `/games/spectate/${gameId}`;

    navigate(path, {
      state: {
        breadcrumbFrom: {
          path: "/games/spectate-auth",
          text: "Spectate Dashboard",
        },
      },
    });
  };

  if (loading) {
    return (
      <p className="loading-message">
        <Zap size={16} className="spin-icon" /> Loading live games...
      </p>
    );
  }

  const sortedGames = [...games].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  return (
    <div className="games-table-container">
      <div className="header-with-actions">
        <h2 className="table-header">Spectate Live Games</h2>
        <button
          className="btn-refresh-manual"
          onClick={fetchGames}
          disabled={isRefreshing}
          title="Refresh Games"
        >
          <RefreshCw size={20} className={isRefreshing ? "spin-icon" : ""} />
        </button>
      </div>

      <p className="mobile-refresh-hint">↓ Pull down to refresh</p>

      <PullToRefresh onRefresh={fetchGames} pullingContent="">
        <div className="refresh-wrapper">
          {error && <p className="error-message">{error}</p>}

          {games.length === 0 ? (
            <div className="no-games-message">
              <p>No games are currently running.</p>
              <p>Check back later!</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className="clan-context-cell">Clan / Event</th>
                  <th>Status</th>
                  <th className="teams-cell">Teams & Positions</th>
                  <th className="action-cell">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedGames.map((game) => {
                  const virtualStatus = getSpectateVirtualStatus(game);
                  return (
                    <tr key={game.id} className="game-row">
                      <td className="clan-context-cell">
                        <span className="clan-name">{game.clan_name}</span>
                        <span className="event-name">{game.event_name}</span>
                      </td>
                      <td>{renderGameStatus(virtualStatus)}</td>
                      <td className="teams-cell">
                        <ul className="team-list">
                          {game.team_states.map((state) => (
                            <li
                              key={state.team_id}
                              style={{ color: state.team_color || "#333" }}
                            >
                              {state.team_name}: {state.current_position}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="action-cell">
                        <button
                          className="btn-action btn-spectate"
                          onClick={() =>
                            handleSpectateGame(
                              game.clan_id,
                              game.event_id,
                              game.id,
                            )
                          }
                        >
                          <Eye size={16} />
                          <span className="button-text">Spectate Live</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
}

export default SpectateTableContent;
